/** 原子件：卡片与 KPI 块，形状对齐原型 `ui.js` 的 `card()` / `kpi()`。 */

import type { ReactNode } from "react";

export interface CardProps {
  title?: ReactNode;
  sub?: ReactNode;
  actions?: ReactNode;
  /** `nopad` 时正文不加内边距——表格类内容要贴边。 */
  nopad?: boolean;
  /** `fatal` / `warn` 给卡片整体上色（对账不平、停机这类）。 */
  cls?: string;
  children: ReactNode;
}

export function Card({ title, sub, actions, nopad, cls, children }: CardProps): ReactNode {
  return (
    <section className={cls ? `card ${cls}` : "card"}>
      {title === undefined ? null : (
        <header>
          {title}
          {sub === undefined ? null : <span className="sub">{sub}</span>}
          {actions === undefined ? null : <span className="actions">{actions}</span>}
        </header>
      )}
      {nopad ? children : <div className="body">{children}</div>}
    </section>
  );
}

export function Kpi({ label, value, detail }: { label: ReactNode; value: ReactNode; detail?: ReactNode }): ReactNode {
  return (
    <div className="card kpi">
      <div className="k">{label}</div>
      <div className="v">{value}</div>
      {detail === undefined ? null : <div className="d">{detail}</div>}
    </div>
  );
}

export function Note({ tone = "", children }: { tone?: "" | "warn" | "bad"; children: ReactNode }): ReactNode {
  return <div className={tone ? `note ${tone}` : "note"}>{children}</div>;
}

export function Kv({ pairs }: { pairs: [ReactNode, ReactNode][] }): ReactNode {
  return (
    <dl className="kv">
      {pairs.map(([key, value], index) => (
        <div key={index} style={{ display: "contents" }}>
          <dt>{key}</dt>
          <dd>{value === null || value === undefined || value === "" ? "—" : value}</dd>
        </div>
      ))}
    </dl>
  );
}

export function PageHead({
  title,
  sub,
  actions,
}: {
  title: ReactNode;
  sub?: ReactNode;
  actions?: ReactNode;
}): ReactNode {
  return (
    <div className="page-head">
      <h1>{title}</h1>
      {sub === undefined ? null : <span className="sub">{sub}</span>}
      {actions === undefined ? null : <span className="actions">{actions}</span>}
    </div>
  );
}

export function Lede({ children }: { children: ReactNode }): ReactNode {
  return <p className="lede">{children}</p>;
}

/** 水平占用条：越 90% 变红、60% 起变黄（口径同原型 `ui.bar`）。 */
export function Bar({ used, limit }: { used: number; limit: number }): ReactNode {
  if (!limit) return null;
  const ratio = Math.max(0, Math.min(1, used / limit));
  const cls = ratio >= 0.9 ? "bad" : ratio >= 0.6 ? "warn" : "";
  return (
    <div className="bar" style={{ marginTop: 5 }}>
      <i className={cls} style={{ width: `${(ratio * 100).toFixed(1)}%` }} />
    </div>
  );
}
